import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR, MONTHS_ID } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import PondokIcon from "../components/PondokIcon";

const formatDateShort = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const today = () =>
  new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function ExpensesPrint() {
  const { year, month } = useParams();  // year required, month optional ("all")
  const yearNum = Number(year);
  const isMonthAll = !month || month === "all";
  const monthNum = isMonthAll ? null : Number(month);

  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("year", String(yearNum));
    if (monthNum) params.set("month", String(monthNum));
    Promise.all([
      api.get(`/expenses?${params.toString()}`),
      api.get(`/settings`),
    ]).then(([e, s]) => {
      setExpenses(e.data || []);
      setSettings(s.data);
    }).finally(() => setLoading(false));
  }, [yearNum, monthNum]);

  if (loading) return <div className="p-12 text-center text-[#586D61]">Memuat…</div>;

  const grandTotal = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  // Group by category
  const byCat = {};
  expenses.forEach((e) => {
    const c = e.category || "Lainnya";
    if (!byCat[c]) byCat[c] = { total: 0, count: 0 };
    byCat[c].total += Number(e.amount || 0);
    byCat[c].count += 1;
  });
  const cats = Object.entries(byCat).sort((a, b) => b[1].total - a[1].total);

  // Group by month if showing whole year
  const byMonth = {};
  if (isMonthAll) {
    expenses.forEach((e) => {
      const ym = (e.date || "").slice(0, 7);
      const m = Number((e.date || "").slice(5, 7));
      if (!ym) return;
      if (!byMonth[m]) byMonth[m] = { total: 0, count: 0 };
      byMonth[m].total += Number(e.amount || 0);
      byMonth[m].count += 1;
    });
  }
  const months = Object.entries(byMonth).sort((a, b) => Number(a[0]) - Number(b[0]));

  // Sort expenses by date asc for printout
  const expensesSorted = [...expenses].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const periodLabel = isMonthAll
    ? `Tahun ${yearNum}`
    : `${MONTHS_ID[monthNum - 1]} ${yearNum}`;

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden bg-white border-b border-[#EBE6DE] px-6 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <Link to="/expenses" className="inline-flex items-center gap-2 text-sm text-[#586D61] hover:text-[#082618]">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Pengeluaran
        </Link>
        <Button
          data-testid="btn-print-expenses"
          onClick={() => window.print()}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl gap-2"
        >
          <Printer className="h-4 w-4" /> Cetak / Save PDF
        </Button>
      </div>

      <div className="max-w-4xl mx-auto p-8 print:p-6">
        {/* Header */}
        <div className="border-b-2 border-[#082618] pb-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-12 w-12 rounded-xl object-contain bg-white border border-[#EBE6DE]" data-testid="expenses-print-logo" />
              ) : (
                <div className="h-12 w-12 rounded-xl bg-[#D16B4D] flex items-center justify-center">
                  <PondokIcon className="h-7 w-7 text-white" strokeWidth={1.8} />
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D16B4D]">PondokRaja</div>
                <div className="font-heading text-2xl font-bold text-[#172E21] leading-tight">
                  {settings?.homestay_name || "Meos Ambower Homestay"}
                </div>
                {settings?.homestay_address && <div className="text-xs text-[#586D61] mt-0.5">{settings.homestay_address}</div>}
              </div>
            </div>
            <div className="text-right text-xs text-[#586D61]">
              <div>Dicetak: {today()}</div>
              <div className="font-mono">{settings?.homestay_email || ""}</div>
              <div className="font-mono">{settings?.homestay_phone || ""}</div>
            </div>
          </div>
          <div className="mt-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Laporan Pengeluaran</div>
              <h1 className="font-heading text-3xl font-bold text-[#172E21] mt-1">{periodLabel}</h1>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Total Pengeluaran</div>
              <div className="font-mono text-2xl font-bold text-[#D14D4D]">-{formatIDR(grandTotal)}</div>
              <div className="text-xs text-[#586D61] mt-0.5">{expenses.length} transaksi</div>
            </div>
          </div>
        </div>

        {expenses.length === 0 ? (
          <div className="text-center py-16 text-[#586D61]">
            Tidak ada data pengeluaran untuk periode {periodLabel}.
          </div>
        ) : (
          <>
            {/* Per-Month summary (only when showing whole year) */}
            {isMonthAll && months.length > 0 && (
              <section className="mb-8">
                <h2 className="font-heading text-lg font-bold text-[#172E21] mb-3">Rekap per Bulan</h2>
                <table className="w-full text-sm border border-[#EBE6DE]">
                  <thead className="bg-[#FDFBF7]">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Bulan</th>
                      <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Transaksi</th>
                      <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map(([m, data]) => (
                      <tr key={m} className="border-b border-[#EBE6DE]/50">
                        <td className="px-3 py-2">{MONTHS_ID[Number(m) - 1]} {yearNum}</td>
                        <td className="px-3 py-2 text-right font-mono">{data.count}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[#D14D4D]">-{formatIDR(data.total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#FDFBF7] font-bold border-t-2 border-[#082618]">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-3 py-2 text-right font-mono">{expenses.length}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#D14D4D]">-{formatIDR(grandTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            )}

            {/* Per-Category summary */}
            <section className="mb-8">
              <h2 className="font-heading text-lg font-bold text-[#172E21] mb-3">Rekap per Kategori</h2>
              <table className="w-full text-sm border border-[#EBE6DE]">
                <thead className="bg-[#FDFBF7]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Kategori</th>
                    <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Transaksi</th>
                    <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Total</th>
                    <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">% dari Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cats.map(([cat, data]) => {
                    const pct = grandTotal > 0 ? (data.total / grandTotal) * 100 : 0;
                    return (
                      <tr key={cat} className="border-b border-[#EBE6DE]/50">
                        <td className="px-3 py-2 font-medium">{cat}</td>
                        <td className="px-3 py-2 text-right font-mono">{data.count}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[#D14D4D]">-{formatIDR(data.total)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#586D61]">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            {/* Detailed transactions */}
            <section>
              <h2 className="font-heading text-lg font-bold text-[#172E21] mb-3">Daftar Transaksi</h2>
              <table className="w-full text-xs border border-[#EBE6DE]">
                <thead className="bg-[#FDFBF7]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE] w-8">#</th>
                    <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Tanggal</th>
                    <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Kategori</th>
                    <th className="text-left px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Deskripsi</th>
                    <th className="text-right px-3 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Jumlah</th>
                  </tr>
                </thead>
                <tbody>
                  {expensesSorted.map((e, idx) => (
                    <tr key={e.id} className="border-b border-[#EBE6DE]/50">
                      <td className="px-3 py-2 text-[#586D61] font-mono">{idx + 1}</td>
                      <td className="px-3 py-2 font-mono">{formatDateShort(e.date)}</td>
                      <td className="px-3 py-2">{e.category || "Lainnya"}</td>
                      <td className="px-3 py-2">
                        <div>{e.description || "—"}</div>
                        {e.notes && <div className="text-[10px] text-[#586D61] italic mt-0.5">{e.notes}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-[#D14D4D]">-{formatIDR(e.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#FDFBF7] font-bold border-t-2 border-[#082618]">
                    <td colSpan={4} className="px-3 py-2 text-right">TOTAL PENGELUARAN</td>
                    <td className="px-3 py-2 text-right font-mono text-[#D14D4D]">-{formatIDR(grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </>
        )}

        {/* Footer signature */}
        <div className="mt-16 grid grid-cols-2 gap-12 print:break-inside-avoid">
          <div className="text-center">
            <div className="text-xs text-[#586D61] mb-16">Disiapkan oleh,</div>
            <div className="border-t border-[#082618] pt-2">
              <div className="text-xs font-medium text-[#172E21]">Staff / Bendahara</div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[#586D61] mb-16">Mengetahui,</div>
            <div className="border-t border-[#082618] pt-2">
              <div className="text-xs font-medium text-[#172E21]">Pemilik Homestay</div>
            </div>
          </div>
        </div>

        {/* Print footer */}
        <div className="mt-8 text-center text-[10px] text-[#586D61]/60 print:fixed print:bottom-4 print:left-0 print:right-0">
          Generated by PondokRaja · pondokraja.com
        </div>
      </div>
    </div>
  );
}
